import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import type { NotificationResponse } from '@/services/api'
import { Card, Table, Pagination, Badge, Button, Select } from '@/components/ui'
import { DEFAULT_PAGE_SIZE } from '@spoofio/shared'
import { formatAuditTimestamp } from '@/utils/format-date'

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'new_signup', label: 'New Signup' },
  { value: 'payment_failure', label: 'Payment Failure' },
  { value: 'trial_expiration', label: 'Trial Expiration' },
  { value: 'high_detection_volume', label: 'High Detection Volume' },
]

const SEVERITY_OPTIONS = [
  { value: '', label: 'All Severities' },
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'critical', label: 'Critical' },
]

const READ_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'false', label: 'Unread' },
  { value: 'true', label: 'Read' },
]

type SeverityVariant = 'primary' | 'warning' | 'danger'

const SEVERITY_BADGE_MAP: Record<string, { label: string; variant: SeverityVariant }> = {
  info: { label: 'Info', variant: 'primary' },
  warning: { label: 'Warning', variant: 'warning' },
  critical: { label: 'Critical', variant: 'danger' },
}

export function AdminNotificationsPage() {
  const queryClient = useQueryClient()
  const [typeFilter, setTypeFilter] = useState('')
  const [severityFilter, setSeverityFilter] = useState('')
  const [readFilter, setReadFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  const offset = (currentPage - 1) * DEFAULT_PAGE_SIZE

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-notifications', typeFilter, severityFilter, readFilter, offset],
    queryFn: () =>
      api.adminListNotifications({
        type: typeFilter || undefined,
        severity: severityFilter || undefined,
        isRead: readFilter ? readFilter === 'true' : undefined,
        limit: DEFAULT_PAGE_SIZE,
        offset,
      }),
  })

  const markRead = useMutation({
    mutationFn: (id: string) => api.adminMarkNotificationRead(id),
    onSuccess: () => { invalidate() },
  })

  const markAllRead = useMutation({
    mutationFn: () => api.adminMarkAllNotificationsRead(),
    onSuccess: () => { invalidate() },
  })

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['admin-notifications'] })
    void queryClient.invalidateQueries({ queryKey: ['admin-notification-unread-count'] })
  }

  const columns = [
    {
      key: 'severity',
      header: '',
      render: (n: NotificationResponse) => <SeverityIcon severity={n.severity} />,
    },
    {
      key: 'title',
      header: 'Title',
      render: (n: NotificationResponse) => (
        <span className={n.isRead ? 'text-gray-600' : 'font-semibold text-gray-900'}>
          {n.title}
        </span>
      ),
    },
    {
      key: 'message',
      header: 'Message',
      render: (n: NotificationResponse) => (
        <span className="text-sm text-gray-500 truncate block max-w-xs">{n.message}</span>
      ),
    },
    {
      key: 'severityBadge',
      header: 'Severity',
      render: (n: NotificationResponse) => {
        const badge = SEVERITY_BADGE_MAP[n.severity]
        return <Badge variant={badge.variant}>{badge.label}</Badge>
      },
    },
    {
      key: 'type',
      header: 'Type',
      render: (n: NotificationResponse) => (
        <span className="text-sm capitalize">{n.type.replace(/_/g, ' ')}</span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Time',
      render: (n: NotificationResponse) => formatAuditTimestamp(n.createdAt),
    },
    {
      key: 'actions',
      header: '',
      render: (n: NotificationResponse) =>
        !n.isRead ? (
          <button
            onClick={() => { markRead.mutate(n.id) }}
            className="text-xs text-primary-600 hover:text-primary-800"
          >
            Mark read
          </button>
        ) : (
          <span className="text-xs text-gray-400">Read</span>
        ),
    },
  ]

  const totalPages = data ? Math.ceil(data.total / DEFAULT_PAGE_SIZE) : 1

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => { markAllRead.mutate() }}
          isLoading={markAllRead.isPending}
        >
          Mark All as Read
        </Button>
      </div>

      <Card>
        <div className="flex flex-wrap gap-4 mb-4">
          <Select
            label="Type"
            name="type"
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1) }}
            options={TYPE_OPTIONS}
            className="w-48"
          />
          <Select
            label="Severity"
            name="severity"
            value={severityFilter}
            onChange={(e) => { setSeverityFilter(e.target.value); setCurrentPage(1) }}
            options={SEVERITY_OPTIONS}
            className="w-40"
          />
          <Select
            label="Status"
            name="readStatus"
            value={readFilter}
            onChange={(e) => { setReadFilter(e.target.value); setCurrentPage(1) }}
            options={READ_OPTIONS}
            className="w-36"
          />
        </div>

        {error ? (
          <div className="text-center py-8">
            <p className="text-danger-600">Failed to load notifications</p>
          </div>
        ) : (
          <>
            <Table
              columns={columns}
              data={data?.data ?? []}
              keyExtractor={(n) => n.id}
              isLoading={isLoading}
              emptyMessage="No notifications found"
              rowClassName={(n: NotificationResponse) => n.isRead ? '' : 'bg-blue-50'}
            />
            {data && data.total > DEFAULT_PAGE_SIZE && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={data.total}
                itemsPerPage={DEFAULT_PAGE_SIZE}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </Card>
    </div>
  )
}

function SeverityIcon({ severity }: { severity: string }) {
  if (severity === 'critical') {
    return (
      <svg className="w-5 h-5 text-danger-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    )
  }
  if (severity === 'warning') {
    return (
      <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  }
  return (
    <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

